import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const CARD_FIELDS =
  "id, slug, title, excerpt, meta_description, cluster, focus_keyword, cover_url, reading_minutes, published_at, quality";

export const listKbArticles = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("kb_articles")
    .select(CARD_FIELDS)
    .eq("published", true)
    .order("published_at", { ascending: false });
  if (error) {
    console.error("[kb list]", error);
    return { articles: [] as any[] };
  }
  return { articles: data ?? [] };
});

export const getKbArticle = createServerFn({ method: "GET" })
  .inputValidator((input) =>
    z.object({ slug: z.string().trim().min(1).max(120).regex(/^[a-z0-9-]+$/) }).parse(input),
  )
  .handler(async ({ data }) => {
    const { data: row, error } = await supabaseAdmin
      .from("kb_articles")
      .select(
        "id, slug, title, excerpt, meta_description, content, faq, tldr, keywords, cluster, focus_keyword, cover_url, reading_minutes, published_at, updated_at, quality",
      )
      .eq("slug", data.slug)
      .eq("published", true)
      .maybeSingle();
    if (error) {
      console.error("[kb get]", error);
      throw new Error("Kunde inte hämta artikeln.");
    }
    return { article: row };
  });

export const listKbSlugs = createServerFn({ method: "GET" }).handler(async () => {
  const { data, error } = await supabaseAdmin
    .from("kb_articles")
    .select("slug, published_at, updated_at")
    .eq("published", true)
    .order("published_at", { ascending: false });
  if (error) return { articles: [] as Array<{ slug: string; published_at: string | null; updated_at: string }> };
  return { articles: data ?? [] };
});

/* --------------------------------------------------------------------- admin */

async function assertAdmin() {
  const { checkIsAdmin } = await import("@/lib/blog.functions");
  const { isAdmin } = await checkIsAdmin();
  if (!isAdmin) throw new Error("Endast admin.");
  return true;
}

export const kbAdminOverview = createServerFn({ method: "GET" }).handler(async () => {
  await assertAdmin();
  const [{ data: articles }, { data: keywords }] = await Promise.all([
    supabaseAdmin
      .from("kb_articles")
      .select("id, slug, title, cluster, status, published, published_at, quality")
      .order("created_at", { ascending: false })
      .limit(200),
    supabaseAdmin
      .from("kb_keywords")
      .select("id, keyword, cluster, priority, status, attempts, last_error, source")
      .order("priority", { ascending: false })
      .limit(300),
  ]);
  const counts = (keywords ?? []).reduce<Record<string, number>>((acc, k) => {
    const s = String((k as any).status);
    acc[s] = (acc[s] ?? 0) + 1;
    return acc;
  }, {});
  return { articles: articles ?? [], keywords: keywords ?? [], counts };
});

export const kbSetPublished = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ id: z.string().uuid(), published: z.boolean() }).parse(input))
  .handler(async ({ data }) => {
    await assertAdmin();
    const { error } = await supabaseAdmin
      .from("kb_articles")
      .update({
        published: data.published,
        status: data.published ? "published" : "review",
        published_at: data.published ? new Date().toISOString() : null,
      })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export const kbDeleteArticle = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ id: z.string().uuid() }).parse(input))
  .handler(async ({ data }) => {
    await assertAdmin();
    const { error } = await supabaseAdmin.from("kb_articles").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });
